import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.components.shortcut-drawer';
import { Add } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Divider,
    FormControlLabel,
    Stack,
    Typography,
} from '@mui/material';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { isAfter, isBefore, isEqual, parseISO, startOfDay, subMonths } from 'date-fns';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import Container from 'src/components/atoms/Container';
import { SalesCampaignsActionsRenderer } from 'src/components/molecules/SalesCampaignActionsRenderer/SalesCampaignActionsRenderer';
import { defaultNonSortableColDef } from 'src/components/organisms/DataTable/columns/default-options';
import { DataTable } from 'src/components/organisms/DataTable/DataTable';
import { DeleteDialog } from 'src/components/organisms/DeleteDialog/DeleteDialog';
import { DialogTitleWithCloseIcon } from 'src/components/organisms/DialogTitleWithCloseIcon/DialogTitleWithCloseIcon';
import { SmartModuleTitleCard } from 'src/components/organisms/SmartModuleTitleCard/SmartModuleTitleCard';
import { TableWrapper } from 'src/components/styled/TableWrapper';
import { useSelectedPartner } from 'src/hooks';
import { useCustomAgGridNavigation } from 'src/hooks/ag-grid/useCustomAgGridNavigation';
import { useCompositePartner } from 'src/hooks/partner/useCompositePartner';
import { useSalesCampaigns } from 'src/hooks/sales-campaigns-planning/useSalesCampaigns';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import { useSnackbar } from 'src/hooks/snackbar/useSnackbar';
import { useFilteredStores } from 'src/hooks/store/useFilteredStores';
import { useStorefrontTypesApiQuery } from 'src/hooks/style-timeline-settings/queries/useStorefrontTypesApiQuery';
import {
    CreateSalesCampaignInput,
    SalesCampaignModel,
    UpdateSalesCampaignInput,
} from 'src/infrastructure/rest-api/api-types';
import { useApiMutation } from 'src/infrastructure/rest-api/useApi';
import { recordError } from 'src/infrastructure/telemetry';
import { getErrorMessage, isClientError } from 'src/utils/api/error-utils';
import { isHTMLElement } from 'src/utils/dom';
import { handleDownload } from 'src/utils/fileUtils';
import { SalesCampaignDialog } from './CreateCampaignDialog';
import { defaultColumns } from './data/columns';

// This is used to represent the total across all stores for PDF downloads
const TOTAL_STORE_ID = -1;

type PdfReportType = 'campaign' | 'monitor';

interface PdfDownloadTarget {
    campaignId: number;
    reportType: PdfReportType;
}

const isMonitorReportAvailable = (campaign: SalesCampaignModelWithoutCompositePartnerId): boolean => {
    const today = startOfDay(new Date());
    const startDate = parseISO(campaign.startDate);
    const endDate = parseISO(campaign.endDate);
    const oldestEligibleEndDate = subMonths(today, 6);

    return (
        (isBefore(startDate, today) || isEqual(startDate, today)) &&
        (isAfter(endDate, oldestEligibleEndDate) || isEqual(endDate, oldestEligibleEndDate))
    );
};

const mapSelectedSalesCampaignToUpdateInput = (
    campaign: SalesCampaignModelWithoutCompositePartnerId
): CreateSalesCampaignInput & UpdateSalesCampaignInput => {
    return {
        endDate: campaign.endDate,
        salesCampaignId: campaign.id,
        startDate: campaign.startDate,
        name: campaign.name ?? '',
        storefrontTypeId: campaign.storefrontType?.id ?? Number.NaN,
        partnerCompositeId: Number.NaN,
    };
};

type SalesCampaignModelWithoutCompositePartnerId = Omit<SalesCampaignModel, 'partnerCompositeId'>;

export const SalesCampaigns: FC = () => {
    const gridApi = useRef<GridApi | null>(null);
    const [filteredStores] = useFilteredStores();
    const { data: storefrontTypes, loading: storefrontTypesLoading } = useStorefrontTypesApiQuery();

    const [deleteCampaign] = useApiMutation('/api/salescampaigns/{id}', 'delete', {
        update(data, _variables, queryClient) {
            const path = { partnerId: compositePartner?.id };
            queryClient.setQueryData(['/api/salescampaigns/partners/{partnerId}', path], data);
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return typeof key === 'string' && key.startsWith('/api/styletimeline/partners/');
                },
            });
        },
    });

    const { data: campaigns, loading: salesCampaignsLoading, refetch: refetchCampaigns } = useSalesCampaigns();
    const [isEditing, setIsEditing] = useState(false);
    const [campaignPendingDeletion, setCampaignPendingDeletion] =
        useState<SalesCampaignModelWithoutCompositePartnerId | null>(null);
    const [selectedSalesCampaign, setSelectedSalesCampaign] =
        useState<SalesCampaignModelWithoutCompositePartnerId | null>(null);
    const [showDialog, setShowDialog] = useState(false);
    const compositePartner = useCompositePartner();
    const [partner] = useSelectedPartner();

    const loading = storefrontTypesLoading || salesCampaignsLoading;

    const showSnackbar = useSnackbar();
    const showChangesSnackbar = useChangesSnackbar();

    const [pdfDownloadTarget, setPdfDownloadTarget] = useState<PdfDownloadTarget | null>(null);

    const handleShowAddDialog = useCallback(() => {
        setShowDialog(true);
        setIsEditing(false);
    }, []);

    useKeyboardShortcut({
        shortcutName: 'NEW',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            handleShowAddDialog();
        },
    });

    const handleDialogClose = useCallback(
        (ok: boolean) => {
            if (ok) {
                refetchCampaigns();
            }
            setShowDialog(false);
            setIsEditing(false);
        },
        [refetchCampaigns]
    );

    const showDeleteDialog = useCallback((campaign: SalesCampaignModelWithoutCompositePartnerId) => {
        setCampaignPendingDeletion(campaign);
    }, []);

    useKeyboardShortcut({
        shortcutName: 'EDIT/VIEW',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApi.current?.getSelectedRows();
            if (selectedRows?.length === 1) {
                if (isHTMLElement(document.activeElement)) {
                    // Blur the active element to prevent focus issues when opening the dialog
                    document.activeElement.blur();
                }
                const campaign = selectedRows[0] as SalesCampaignModelWithoutCompositePartnerId;
                setSelectedSalesCampaign(campaign);
                setShowDialog(true);
                setIsEditing(true);
            } else {
                showSnackbar('Please select a single campaign to edit.', 'warning');
            }
        },
    });

    useKeyboardShortcut({
        shortcutName: 'DELETE',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApi.current?.getSelectedRows();
            if (selectedRows?.length === 1) {
                const campaign = selectedRows[0] as SalesCampaignModelWithoutCompositePartnerId;
                showDeleteDialog(campaign);
            } else {
                showSnackbar('Please select a single campaign to delete.', 'warning');
            }
        },
    });

    const handleDelete = useCallback(async () => {
        if (campaignPendingDeletion) {
            await deleteCampaign({ path: { id: campaignPendingDeletion.id } })
                .then(() => {
                    showChangesSnackbar();
                    refetchCampaigns();
                })
                .catch((err) => {
                    showChangesSnackbar(err);
                })
                .finally(() => {
                    setCampaignPendingDeletion(null);
                });
        }
    }, [campaignPendingDeletion, deleteCampaign, refetchCampaigns, showChangesSnackbar]);

    const handleCancelDeletion = useCallback(() => {
        setCampaignPendingDeletion(null);
    }, []);

    const handleEdit = useCallback((campaign: SalesCampaignModelWithoutCompositePartnerId) => {
        setSelectedSalesCampaign(campaign);
        setShowDialog(true);
        setIsEditing(true);
    }, []);

    const [isDownloadingPdfs, setIsDownloadingPdfs] = useState(false);
    const [selectedStores, setSelectedStores] = useState<number[]>([]);
    const toggleStoreSelection = useCallback(
        (storeId: number) => {
            if (pdfDownloadTarget?.reportType === 'monitor') {
                if (storeId === TOTAL_STORE_ID) {
                    setSelectedStores(selectedStores.includes(TOTAL_STORE_ID) ? [] : [TOTAL_STORE_ID]);
                    return;
                }

                const individuallySelectedStores = selectedStores.filter((id) => id !== TOTAL_STORE_ID);
                setSelectedStores(
                    individuallySelectedStores.includes(storeId)
                        ? individuallySelectedStores.filter((id) => id !== storeId)
                        : [...individuallySelectedStores, storeId]
                );
                return;
            }

            if (selectedStores.includes(storeId)) {
                setSelectedStores(selectedStores.filter((id) => id !== storeId));
            } else {
                setSelectedStores([...selectedStores, storeId]);
            }
        },
        [pdfDownloadTarget?.reportType, selectedStores]
    );

    const handleShowDownloadDialog = useCallback((id: number) => {
        setPdfDownloadTarget({ campaignId: id, reportType: 'campaign' });
        setSelectedStores([]);
    }, []);

    const handleShowMonitorDownloadDialog = useCallback((id: number) => {
        setPdfDownloadTarget({ campaignId: id, reportType: 'monitor' });
        setSelectedStores([]);
    }, []);

    const pdfDownloadCampaign = useMemo(
        () => campaigns?.find((campaign) => campaign.id === pdfDownloadTarget?.campaignId),
        [campaigns, pdfDownloadTarget]
    );

    const campaignStores = useMemo(() => {
        if (!pdfDownloadCampaign) return filteredStores;
        return filteredStores.filter((store) => store.storefrontType === pdfDownloadCampaign.storefrontType.name);
    }, [filteredStores, pdfDownloadCampaign]);

    const handlePdfDownload = useCallback(
        ({ campaignId, reportType }: PdfDownloadTarget) => {
            setIsDownloadingPdfs(true);
            const isMonitorReport = reportType === 'monitor';
            const generatePdfUrl = isMonitorReport
                ? `/api/salescampaigns/${campaignId}/monitor/pdf`
                : `/api/salesCampaigns/${campaignId}/pdf`;
            const filename = isMonitorReport ? 'sales-campaign-monitor-report.pdf' : 'sales-campaigns-reports.zip';
            handleDownload(filename, generatePdfUrl, 'POST', selectedStores)
                .then(() => {
                    showSnackbar('Your files are being downloaded', {
                        variant: 'success',
                        autoHideDuration: 3000,
                    });
                })
                .catch((err) => {
                    showSnackbar(getErrorMessage(err, 'Could not download files'), 'error');
                    !isClientError(err) && recordError(err);
                })
                .finally(() => {
                    setIsDownloadingPdfs(false);
                    setPdfDownloadTarget(null);
                });
        },
        [selectedStores, showSnackbar]
    );

    const columns = useMemo(() => {
        return [
            ...defaultColumns,
            {
                headerName: 'Actions',
                field: 'actions',
                cellRenderer: SalesCampaignsActionsRenderer,
            },
        ];
    }, []);

    const tableWrapperRef = useRef<HTMLDivElement>(null);

    const handleGridReady = useCallback(({ api }: GridReadyEvent) => {
        gridApi.current = api;
    }, []);

    useCustomAgGridNavigation({
        api: gridApi.current,
        tableWrapperRef,
    });

    useKeyboardShortcut({
        shortcutName: 'NEW',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            handleShowAddDialog();
        },
    });

    const isMonitorPdfDownload = pdfDownloadTarget?.reportType === 'monitor';
    let downloadDialogDescription = 'Select stores to include in PDF report';
    if (isDownloadingPdfs) {
        const fileLabel = selectedStores.length > 1 ? 'files' : 'file';
        downloadDialogDescription = `Please wait while we prepare your ${fileLabel}...`;
    } else if (isMonitorPdfDownload) {
        downloadDialogDescription = 'Download for all stores or select stores individually';
    }

    let totalStoreLabel = 'Total';
    if (isMonitorPdfDownload) {
        totalStoreLabel = 'All stores';
    } else if (partner?.name) {
        totalStoreLabel = `Total - ${partner.name}`;
    }

    return (
        <>
            <SmartModuleTitleCard />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    paddingTop: 2,
                    paddingBottom: 2,
                }}
            >
                <Button
                    data-testid="addCampaign"
                    className="add-campaign-button"
                    size="medium"
                    variant="contained"
                    color="info"
                    startIcon={<Add />}
                    onClick={handleShowAddDialog}
                >
                    Add campaign
                </Button>
            </Box>
            <Container data-testid="salesCampaigns" style={{ height: '100%' }}>
                {!loading && campaigns && campaigns.length > 0 && (
                    <TableWrapper
                        sx={{ flex: 1 }}
                        className="ag-theme-alpine-custom-compact"
                        data-testid="salesCampaignsList"
                        ref={tableWrapperRef}
                    >
                        <DataTable
                            rowSelection={{
                                mode: 'singleRow',
                                checkboxes: false,
                                enableClickSelection: true,
                            }}
                            domLayout="normal"
                            cellSelection={false}
                            suppressCellFocus
                            autoSizeToFit
                            isNumberTable={false}
                            loading={loading}
                            columns={columns}
                            defaultColDef={defaultNonSortableColDef}
                            rows={campaigns}
                            context={{
                                handleEdit,
                                showDeleteDialog,
                                handleShowDownloadDialog,
                                handleShowMonitorDownloadDialog,
                                isMonitorReportAvailable,
                            }}
                            onGridReady={handleGridReady}
                        />
                    </TableWrapper>
                )}
                {!loading && campaigns && campaigns.length < 1 && (
                    <Stack
                        data-testid="emptySalesCampaignsList"
                        sx={{
                            alignItems: 'center',
                            gap: '0.5rem',
                            mt: '1.5rem',
                        }}
                    >
                        <Typography
                            sx={{
                                fontStyle: 'italic',
                            }}
                        >
                            No campaigns yet. Add one to get started.
                        </Typography>
                    </Stack>
                )}
                {campaignPendingDeletion && (
                    <DeleteDialog
                        open={!!campaignPendingDeletion}
                        onCancel={handleCancelDeletion}
                        onDelete={handleDelete}
                        title="Delete campaign?"
                        content={`Are you sure you want to delete campaign "${campaignPendingDeletion.name}"? Deleting this campaign is permanent and cannot be undone. All changes related to this campaign will also be deleted.`}
                    />
                )}
            </Container>
            {pdfDownloadTarget && (
                <Dialog open={true} onClose={() => setPdfDownloadTarget(null)} maxWidth="sm" fullWidth={true}>
                    <DialogTitleWithCloseIcon
                        title={isMonitorPdfDownload ? 'Download latest week performance' : 'Download PDF reports'}
                        handleClose={() => setPdfDownloadTarget(null)}
                    />
                    <DialogContent>
                        <DialogContentText
                            sx={{
                                textAlign: 'center',
                                pb: 2,
                            }}
                        >
                            {downloadDialogDescription}
                        </DialogContentText>

                        {isDownloadingPdfs && (
                            <Stack
                                sx={{
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 2,
                                }}
                            >
                                <CircularProgress />
                            </Stack>
                        )}
                        {!isDownloadingPdfs && (
                            <Stack
                                sx={{
                                    gap: 2,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: 'flex-end',
                                        gap: 2,
                                    }}
                                >
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() =>
                                            setSelectedStores(
                                                isMonitorPdfDownload
                                                    ? [TOTAL_STORE_ID]
                                                    : [TOTAL_STORE_ID, ...campaignStores.map((store) => store.id)]
                                            )
                                        }
                                    >
                                        {isMonitorPdfDownload ? 'All stores' : 'Select all'}
                                    </Button>
                                    <Button fullWidth variant="outlined" onClick={() => setSelectedStores([])}>
                                        Deselect all
                                    </Button>
                                </Stack>
                                <Stack>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedStores.includes(TOTAL_STORE_ID)}
                                                onChange={(_event) => {
                                                    toggleStoreSelection(TOTAL_STORE_ID);
                                                }}
                                            />
                                        }
                                        label={totalStoreLabel}
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                    <Divider />
                                    {campaignStores.map((store) => (
                                        <FormControlLabel
                                            key={store.id}
                                            control={
                                                <Checkbox
                                                    checked={selectedStores.includes(store.id)}
                                                    onChange={(_event) => {
                                                        toggleStoreSelection(store.id);
                                                    }}
                                                />
                                            }
                                            label={store.storeName}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        )}
                    </DialogContent>
                    {!isDownloadingPdfs && (
                        <DialogActions>
                            <Button variant="text" onClick={() => setPdfDownloadTarget(null)}>
                                Cancel
                            </Button>
                            <Button
                                disabled={selectedStores.length < 1}
                                onClick={() => handlePdfDownload(pdfDownloadTarget)}
                                variant="contained"
                                color="info"
                            >
                                Download {isMonitorPdfDownload ? 'PDF' : 'PDFs'}
                            </Button>
                        </DialogActions>
                    )}
                </Dialog>
            )}
            {showDialog && (
                <SalesCampaignDialog
                    storefrontTypes={storefrontTypes ?? []}
                    isEditing={isEditing}
                    defaultValue={
                        isEditing && selectedSalesCampaign
                            ? mapSelectedSalesCampaignToUpdateInput(selectedSalesCampaign)
                            : {
                                  partnerCompositeId: compositePartner?.id ?? Number.NaN,
                                  name: '',
                                  startDate: '',
                                  endDate: '',
                                  storefrontTypeId: Number.NaN,
                                  salesCampaignId: Number.NaN,
                              }
                    }
                    open={showDialog}
                    onClose={handleDialogClose}
                />
            )}
        </>
    );
};
